import { useMemo } from "react";
import styled from "styled-components";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { useGetAdminLicenseStatsQuery } from "../../query/adminLicenseStatsQuery";
import { isSalesToday } from "../../utils/dailySalesUtils";
import { LoadingContainer } from "../../components/LoadingContainer";
import { Card } from "../../components/Card";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useTranslation } from "react-i18next";

const LICENSE_TYPE_COLORS: Record<string, string> = {
  [BillingItemType.PACKAGE]: colors.primary,
  [BillingItemType.ADD_ON]: colors.secondary,
  [BillingItemType.PAYMENT_METHOD]: colors.success,
  [BillingItemType.EXTRA_DEVICE]: colors.info,
};

const ChartCard = styled(Card)`
  padding: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.large}px;
`;

const EmptyStateMessage = styled.div`
  padding: ${spacing.x4}px;
  text-align: center;
  color: ${colors.textMuted};
`;

const TooltipContent = styled.div`
  padding: ${spacing.x1}px;
  background: white;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${fontSizes.small}px;
`;

interface LicenseTypesPieChartProps {
  date: string;
  selectedDateLabel: string;
}

export const LicenseTypesPieChart = ({
  date,
  selectedDateLabel,
}: LicenseTypesPieChartProps) => {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useGetAdminLicenseStatsQuery(date, date);
  const isToday = isSalesToday(date);

  const chartData = useMemo(() => {
    const dayStats = stats?.[0];
    if (!dayStats?.licensesByType) return [];
    const byType = dayStats.licensesByType;
    return [
      BillingItemType.PACKAGE,
      BillingItemType.ADD_ON,
      BillingItemType.PAYMENT_METHOD,
      BillingItemType.EXTRA_DEVICE,
    ]
      .map((type) => ({
        name: t("billingItems.type." + type),
        value: byType[type] ?? 0,
        type,
      }))
      .filter((d) => d.value > 0);
  }, [stats, t]);

  const chartTitle = isToday
    ? t("adminLicenseStats.licenseTypesToday")
    : t("adminLicenseStats.licenseTypesOnDate", { date: selectedDateLabel });

  const emptyMessage = isToday
    ? t("adminLicenseStats.noDataToday")
    : t("adminLicenseStats.noDataOnDate", { date: selectedDateLabel });

  if (isLoading && chartData.length === 0) {
    return (
      <ChartCard>
        <LoadingContainer message={t("statistics.loading")} />
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <ChartTitle>{chartTitle}</ChartTitle>
      {chartData.length === 0 ? (
        <EmptyStateMessage>{emptyMessage}</EmptyStateMessage>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
            >
              {chartData.map((entry) => (
                <Cell key={entry.type} fill={LICENSE_TYPE_COLORS[entry.type]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [value, ""]}
              content={({ active, payload }) =>
                active && payload?.[0] ? (
                  <TooltipContent>
                    {payload[0].payload.name}: {payload[0].payload.value}
                  </TooltipContent>
                ) : null
              }
            />
            <Legend wrapperStyle={{ fontSize: fontSizes.small }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};
